import React, {ComponentProps, ComponentType, ReactNode, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  getProcessModalReasons,
  IVRetryModal,
  IVProcessModal,
  TranslationFn,
} from '@chekin/shared';
import {PATHS} from 'routes/constants';
import {IV_DOCUMENT_IMAGE_TYPES, STATUSES} from '@chekin/core';
import {Button} from '@chekin/guestapp-ui-kit';
import {ModalTwoButtonsWrapper} from '@guestapp/old-ui-kit';

type DefaultErrorModalProps = {
  message: string;
  description: string;
  children?: ReactNode;
};

type UseIVModalComponentsProps = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  layout: ComponentProps<typeof IVProcessModal>['layoutMode'];
  required: ComponentProps<typeof IVProcessModal>['isRequired'];
  t: TranslationFn;
  errorMessage?: string;
  failedImage?: string;
  processState: STATUSES;
  isProcessModalOpen: boolean;
  closeAndCleanLoadingModal: () => void;
  isRetryModalOpen: boolean;
  handleRetryAgain: () => void;
  handleCancelRetryAgain: () => void;
  DefaultErrorModal: ComponentType<DefaultErrorModalProps>;
  closeErrorModal: () => void;
  continueRegistration: () => void;
};

function useIVModalComponents({
  docType,
  layout,
  required,
  t,
  errorMessage,
  failedImage,
  processState,
  isProcessModalOpen,
  closeAndCleanLoadingModal,
  isRetryModalOpen,
  handleRetryAgain,
  handleCancelRetryAgain,
  DefaultErrorModal,
  closeErrorModal,
  continueRegistration,
}: UseIVModalComponentsProps) {
  const navigate = useNavigate();

  const ProcessModal = useCallback(() => {
    const reasons = getProcessModalReasons(t, docType);

    return (
      <IVProcessModal
        open={isProcessModalOpen}
        errorMessage={errorMessage}
        failedImage={failedImage}
        reasons={reasons}
        state={processState}
        onTryAgain={closeAndCleanLoadingModal}
        docType={docType}
        layoutMode={layout}
        isRequired={required}
        onVerifyLater={() => {
          navigate(`../../${PATHS.addPersonalDataForm}`);
        }}
      />
    );
  }, [
    closeAndCleanLoadingModal,
    docType,
    errorMessage,
    failedImage,
    isProcessModalOpen,
    layout,
    processState,
    required,
    navigate,
    t,
  ]);

  const RetryModal = useCallback(() => {
    return (
      <IVRetryModal
        open={isRetryModalOpen}
        onClickTryAgain={handleRetryAgain}
        onCancel={handleCancelRetryAgain}
      />
    );
  }, [handleCancelRetryAgain, handleRetryAgain, isRetryModalOpen]);

  const ErrorModal = useCallback(() => {
    return (
      <DefaultErrorModal
        message={t('iv_error_message')}
        description={t('iv_error_description')}
      >
        <ModalTwoButtonsWrapper className="kiosko:mt-5">
          <Button
            onClick={() => {
              closeErrorModal();
            }}
          >
            {t('try_again')}
          </Button>
          <Button onClick={continueRegistration} variant="secondary">
            {t('continue_the_registration')}
          </Button>
        </ModalTwoButtonsWrapper>
      </DefaultErrorModal>
    );
  }, [DefaultErrorModal, closeErrorModal, continueRegistration, t]);

  return {ProcessModal, RetryModal, ErrorModal};
}

export {useIVModalComponents};
